import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import HighlightedItem from '@pelckmans/elementis/components/highlighted-item';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SCORE_STATUS } from '../../../../constants/ScoreStatus';
import ModalContext from '../../../../contexts/ModalContext';
import ScorableProgress from '../../../../pages/material/toc/components/ScorableProgress';
import { calculateScore, formatAbsoluteScore } from '@pelckmans/elementis/components/results';
import { InitAssignmentSessionResponse, InitFreeExerciseSessionResponse } from '../exercise-manager/api/exercises-api';
import {
  ConfirmationModalTranslations,
  ScorableAssignmentLtiUserMaterial,
  ScorableFreeExerciseLtiUserMaterial,
  ScorableLtiUserMaterial,
} from './types';

function getPercentage(score: ScorableLtiUserMaterial['score']) {
  if (score.status === SCORE_STATUS.PENDING_GRADING) return 100;

  return calculateScore(score);
}

type ConfirmationDialogProps = {
  id: string;
  material: ScorableAssignmentLtiUserMaterial | ScorableFreeExerciseLtiUserMaterial;
  onConfirm: () => void;
  translations: ConfirmationModalTranslations;
  session: InitAssignmentSessionResponse | InitFreeExerciseSessionResponse;
  isOwner: boolean;
  tags: ReactNode;
};

export default function ConfirmationDialog({
  id,
  material,
  translations,
  session,
  onConfirm,
  isOwner,
  tags,
}: ConfirmationDialogProps) {
  const { closeModal } = useContext(ModalContext);

  const { t } = useTranslation();

  function handleClose() {
    closeModal(id);
  }

  if (!session.result) throw new Error('Session result is missing');

  const isRepeatable = isOwner || !material.assignment || material.assignment.repeatable;

  return (
    <Dialog id={id} modal onClose={handleClose} size="md" aria-label={translations.ariaLabel}>
      <Dialog.Header title={translations.title} onClose={handleClose} aria-label={t('ACTIONS.CLOSE')} />
      <Dialog.Body>
        <div className="confirmation-content-container">
          <p className="confirmation-text">
            {isRepeatable ? translations.body.repeatable : translations.body.notRepeatable}
          </p>
          <HighlightedItem
            label={translations.body.exerciseTitle}
            variant="exercise"
            exerciseName={material.name}
            result={{
              title: translations.body.currentResult,
              value: <ScorableProgress score={material.score} showTooltip />,
            }}
            tags={{
              children: tags,
              'aria-label': undefined,
              role: undefined,
            }}
            percentage={
              material.score.status === SCORE_STATUS.PENDING_GRADING
                ? undefined
                : {
                    title: translations.body.percent,
                    value: getPercentage({
                      ...session.result.score,
                      status: material.score.status,
                      amountOfItems: material.score.amountOfItems,
                    }),
                  }
            }
            score={
              material.score.status === SCORE_STATUS.PENDING_GRADING
                ? undefined
                : {
                    title: translations.body.score,
                    value: formatAbsoluteScore(session.result.score),
                  }
            }
          />
        </div>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={
          isRepeatable
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Button prominence="neutral" onClick={onConfirm}>
                    {translations.actions.restart}
                  </Button>
                ),
              }
            : undefined
        }
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <Button prominence="neutral" onClick={handleClose}>
              {translations.actions.cancel}
            </Button>
          ),
        }}
      />
    </Dialog>
  );
}
